import React from "react";
import {
  PictureAsPdf as PdfIcon,
  Article as WordIcon,
  TextSnippet as TxtIcon,
  Description as DocumentIcon,
} from "@mui/icons-material";

/** The extensions /upload-template accepts; it validates on extension only. */
const VALID_EXTENSIONS = [".pdf", ".docx", ".doc", ".txt", ".rtf"];

export const SUPPORTED_EXTENSIONS_LABEL = VALID_EXTENSIONS.join(", ");

export const validateFile = (
  file: File,
): { valid: boolean; error?: string } => {
  // Extension is the server's sole criterion, so mirror it exactly — matching
  // on MIME type instead would let a .exe through on a text/plain type and
  // fail at /upload-template after the user has already queued it.
  const dot = file.name.lastIndexOf(".");
  if (dot <= 0 || dot === file.name.length - 1) {
    return {
      valid: false,
      error: `File has no extension. Supported: ${SUPPORTED_EXTENSIONS_LABEL}`,
    };
  }

  const fileExtension = file.name.slice(dot).toLowerCase();
  if (!VALID_EXTENSIONS.includes(fileExtension)) {
    return {
      valid: false,
      error: `Unsupported file type: ${fileExtension}. Supported: ${SUPPORTED_EXTENSIONS_LABEL}`,
    };
  }

  const maxSize = 500 * 1024 * 1024; // 500MB
  if (file.size > maxSize) {
    return { valid: false, error: "File size is too large" };
  }

  return { valid: true };
};

export const getFileIconColor = (fileName: string): string => {
  const ext = fileName.toLowerCase().split(".").pop();
  switch (ext) {
    case "pdf":
      return "#f44336";
    case "doc":
    case "docx":
      return "#2196f3";
    case "txt":
      return "#4caf50";
    case "rtf":
      return "#ff9800";
    default:
      return "#9e9e9e";
  }
};

export const getFileIcon = (fileName: string): React.ReactElement => {
  const ext = fileName.toLowerCase().split(".").pop();
  const color = getFileIconColor(fileName);
  const sx = { color, fontSize: 24 };
  switch (ext) {
    case "pdf":
      return React.createElement(PdfIcon, { sx });
    case "doc":
    case "docx":
      return React.createElement(WordIcon, { sx });
    case "txt":
      return React.createElement(TxtIcon, { sx });
    default:
      return React.createElement(DocumentIcon, { sx });
  }
};

export const getFilenameFromDisposition = (
  header?: string,
  fallback = "template.txt",
): string => {
  if (!header) return fallback;
  const match = header.match(
    /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i,
  );
  if (!match) return fallback;
  const value = match[1] || match[2];
  if (!value) return fallback;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return "N/A";
  const parsedDate = new Date(dateString);
  if (Number.isNaN(parsedDate.getTime())) {
    return "N/A";
  }
  return parsedDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * /upload-template accepts 25, but the downstream generate step hard-rejects
 * more than 10 example reports and the job ends `failed` — so 10 is the real
 * ceiling and the cap belongs here, before anything is uploaded.
 */
export const MAX_FILES = 10;
